import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  formatStructuralAnalysisRangeLabel,
  type ChapterView,
  type StructuralAnalysisScopeSelection,
} from "@/features/story/story.types";

type StructuralAnalysisScopeDialogProps = {
  busy?: boolean;
  chapters: Array<Pick<ChapterView, "id" | "sortOrder" | "title">>;
  creditCost: number;
  onOpenChange: (open: boolean) => void;
  onSubmit: (scope: StructuralAnalysisScopeSelection) => Promise<unknown> | void;
  open: boolean;
};

export function StructuralAnalysisScopeDialog({
  busy = false,
  chapters,
  creditCost,
  onOpenChange,
  onSubmit,
  open,
}: StructuralAnalysisScopeDialogProps) {
  const { t } = useTranslation();
  const orderedChapters = useMemo(
    () => [...chapters].sort((left, right) => left.sortOrder - right.sortOrder),
    [chapters],
  );
  const [scopeKind, setScopeKind] = useState<"full-story" | "chapter-range">(
    "full-story",
  );
  const [startChapterId, setStartChapterId] = useState(orderedChapters[0]?.id ?? "");
  const [endChapterId, setEndChapterId] = useState(
    orderedChapters.at(-1)?.id ?? orderedChapters[0]?.id ?? "",
  );
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setScopeKind("full-story");
    setStartChapterId(orderedChapters[0]?.id ?? "");
    setEndChapterId(orderedChapters.at(-1)?.id ?? orderedChapters[0]?.id ?? "");
    setLocalError(null);
  }, [open, orderedChapters]);

  const startIndex = orderedChapters.findIndex(
    (chapter) => chapter.id === startChapterId,
  );
  const endIndex = orderedChapters.findIndex((chapter) => chapter.id === endChapterId);
  const rangeLabel =
    startIndex >= 0 && endIndex >= startIndex
      ? formatStructuralAnalysisRangeLabel(
          orderedChapters[startIndex]!.sortOrder,
          orderedChapters[endIndex]!.sortOrder,
        )
      : null;

  async function handleSubmit() {
    try {
      if (scopeKind === "full-story") {
        setLocalError(null);
        await onSubmit({ scopeKind: "full-story" });
        onOpenChange(false);
        return;
      }

      if (startIndex < 0 || endIndex < 0) {
        setLocalError(t("story.ai.structural.scope.errors.invalidRange"));
        return;
      }

      if (startIndex > endIndex) {
        setLocalError(t("story.ai.structural.scope.errors.reversedRange"));
        return;
      }

      setLocalError(null);
      await onSubmit({
        endChapterId,
        scopeKind: "chapter-range",
        startChapterId,
      });
      onOpenChange(false);
    } catch (error) {
      setLocalError(
        error instanceof Error ? error.message : t("story.ai.structural.errors.failed"),
      );
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("story.ai.structural.scope.title")}</DialogTitle>
          <DialogDescription>
            {t("story.ai.structural.scope.body")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="structural-scope-kind">
              {t("story.ai.structural.scope.scopeLabel")}
            </Label>
            <Select onValueChange={(value) => setScopeKind(value as typeof scopeKind)} value={scopeKind}>
              <SelectTrigger id="structural-scope-kind">
                <SelectValue placeholder={t("story.ai.structural.scope.scopePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full-story">
                  {t("story.ai.structural.scope.allChapters")}
                </SelectItem>
                <SelectItem value="chapter-range">
                  {t("story.ai.structural.scope.selectedRange")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scopeKind === "chapter-range" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="structural-range-start">
                  {t("story.ai.structural.scope.startChapter")}
                </Label>
                <Select onValueChange={setStartChapterId} value={startChapterId}>
                  <SelectTrigger id="structural-range-start">
                    <SelectValue placeholder={t("story.ai.structural.scope.startPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {orderedChapters.map((chapter) => (
                      <SelectItem key={chapter.id} value={chapter.id}>
                        {`Chapter ${chapter.sortOrder + 1}: ${chapter.title}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="structural-range-end">
                  {t("story.ai.structural.scope.endChapter")}
                </Label>
                <Select onValueChange={setEndChapterId} value={endChapterId}>
                  <SelectTrigger id="structural-range-end">
                    <SelectValue placeholder={t("story.ai.structural.scope.endPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {orderedChapters.map((chapter) => (
                      <SelectItem key={chapter.id} value={chapter.id}>
                        {`Chapter ${chapter.sortOrder + 1}: ${chapter.title}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}

          <div className="rounded-[22px] border border-dashed border-border/70 bg-secondary/30 px-4 py-4 text-sm text-muted-foreground">
            {scopeKind === "full-story"
              ? t("story.ai.structural.scope.previewAll", { count: orderedChapters.length })
              : rangeLabel
                ? t("story.ai.structural.scope.previewRange", { rangeLabel })
                : t("story.ai.structural.scope.previewFallback")}
          </div>

          <div className="rounded-[22px] border border-white/70 bg-white/80 px-4 py-4 text-sm text-foreground">
            {t("story.ai.structural.scope.costPreview", { cost: creditCost })}
          </div>

          {localError ? (
            <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {localError}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            {t("story.ai.structural.scope.cancel")}
          </Button>
          <Button
            disabled={busy || orderedChapters.length === 0}
            onClick={() => void handleSubmit()}
            type="button"
          >
            {busy ? t("story.ai.structural.running") : t("story.ai.structural.scope.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
